import {AfterViewInit, Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {NgbCalendar, NgbDateStruct, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventBus} from '../../lib/EventBus';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {EventData} from '../../model/EventData';
import {ParamsModalComponent} from '../../components/params-modal/params-modal.component';
import {AboutComponent} from '../about/about.component';
import {ChangeContext, Options} from 'ngx-slider-v2';
import {FaIconLibrary} from "@fortawesome/angular-fontawesome";
import {fas} from "@fortawesome/free-solid-svg-icons";
import {far} from "@fortawesome/free-regular-svg-icons";

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class MainComponent implements OnInit, AfterViewInit {
  paramForm: UntypedFormGroup;
  mapData: any = {};
  mapParams: {
    location: string;
    currentTick?: number;
    transparentThreshold: number;
    delay: number;
    duration: number;
    trackOpacity: number;
    trackWidth: number;
    trackMarkerSize: number;
    markerSize: number;
    trackColor: string;
    markerColor: string,
    startDate: number,
    paused: boolean,
    loop: boolean,
    customParams: any
  };
  maxDate: NgbDateStruct;
  loop = true;
  paused = false;
  sliderOptions: Options = {
    showTicks: true,
    floor: 0, ceil: 100, animate: false,
    tickStep: 24
  };
  sidebarOpen = false;
  durations = [
    {val: '1', label: '24 hours'},
    {val: '2', label: '2 days'},
    {val: '3', label: '3 days'},
    {val: '4', label: '4 days'},
    {val: '5', label: '5 days'},
    {val: '6', label: '6 days'},
    {val: '7', label: '7 days'}
  ];
  //
  locations = [
    'Global',
    'Rotterdam',
    'Singapore',
    'Shanghai',
    'Hong Kong',
    'Brest',
    'Bonifacio',
    'Audierne',
    'Cape Town',
    'Dubai',
    'Marseille',
    'Athens',
    'Lisbon',
    'San Francisco',
    'Los Angeles',
    'New-York',
    'Seattle',
    'Miami',
    'Oslo',
    'Stockholm',
    'Port Hedland',
    'Jakarta',
    'Tokyo'
  ];
  private bucketspan: number;
  gts: number;
  count: number;
  displaySelection = false;
  selectedShips = [];
  isImgLoaded = false;
  showPic = false;
  pic: string;
  currentTick = 0;
  lastbucket: number;
  private previousPauseState: boolean;

  constructor(
    library: FaIconLibrary,
    private eventBus: EventBus,
    private calendar: NgbCalendar,
    private formBuilder: UntypedFormBuilder,
    private modalService: NgbModal
  ) {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    library.addIconPacks(fas, far);
  }

  ngOnInit(): void {
    this.maxDate = this.calendar.getToday();
    this.eventBus.on('gts', gts => {
      this.gts = gts.gts;
      this.count = gts.count;
    });
    this.eventBus.on('SelectedShips', selected => {
      this.displaySelection = !!selected;
      requestAnimationFrame(() => this.selectedShips = [...selected]);
    });
    this.eventBus.on('LoadShips', () => {
      this.displaySelection = true;
      this.selectedShips = undefined;
      this.pic = undefined;
    });
    this.eventBus.on('mapDataSlider', mapData => {
      console.log(mapData)
      this.sliderOptions.ceil = mapData.bucketcount;
      this.sliderOptions.tickStep = mapData.bucketcount / (24 * mapData.duration);
      this.mapParams.startDate = mapData.lastbucket / 1000 - mapData.bucketcount * mapData.bucketspan;
      this.bucketspan = mapData.bucketspan;
      this.lastbucket = mapData.lastbucket;
      this.sliderOptions = {...this.sliderOptions};
    });
    this.eventBus.on('mapData', t => this.currentTick = t || 0);
    this.eventBus.on('mapDataPause', pause => {
      this.mapData.pause = pause;
      setTimeout(() => this.paused = !!pause);
    });
    this.paramForm = this.formBuilder.group({
      dateField: {value: this.calendar.getToday(), disabled: true},
      durationField: [{value: '1', disabled: true}],
      locationField: ['Global']
    });
    this.mapParams = {
      startDate: dayjs().tz('UTC').subtract(1, 'day').valueOf(),
      location: 'Global',
      loop: true,
      paused: false,
      markerColor: '#D81B60',
      trackColor: '#00897B',
      markerSize: 5,
      trackMarkerSize: 4,
      trackWidth: 2,
      trackOpacity: 0.05,
      delay: 50,
      transparentThreshold: 225,
      duration: 1,
      customParams: {}
    };
    this.sliderOptions = {
      floor: 0,
      ceil: 2000,
      showTicks: true,
      tickStep: 24,
      translate: (value): string => {
        const d = dayjs.tz(this.mapParams.startDate, 'UTC')
          .add(value * this.bucketspan, 'millisecond');
        return d.tz(dayjs.tz.guess()).format('YYYY-MM-DD HH:mm');
      }
    };

    this.paramForm.valueChanges.subscribe(val => {
      this.mapParams.paused = this.paused;
      this.mapParams.loop = this.loop;
      if (!!val.locationField && val.locationField !== this.mapParams.location) {
        this.mapParams.location = val.locationField;
        if (val.locationField === 'Global') {
          this.mapParams.startDate = dayjs().tz('UTC').subtract(1, 'day').valueOf();
          this.mapParams.duration = 1;
          this.paramForm.get('durationField').disable();
          this.paramForm.get('dateField').disable();
        } else {
          this.paramForm.get('durationField').enable();
          this.paramForm.get('dateField').enable();
        }
      } else {
        const now = dayjs().tz(dayjs.tz.guess()).tz('UTC');
        this.mapParams.duration = parseInt(val.durationField || '1', 10);
        if (!!val.dateField) {
          const dateStr = val.dateField.year + '-' + val.dateField.month + '-' + val.dateField.day
            + ' ' + now.hour() + ':' + now.minute() + ':' + now.second();
          const d = dayjs.tz(dayjs(dateStr, 'YYYY+-MM-DD HH:mm:ss'), dayjs.tz.guess())
            .subtract(this.mapParams.duration, 'day');
          this.mapParams.startDate = d.tz('UTC').valueOf();
        }
      }
      if (val.locationField && val.dateField && val.durationField) {
        this.eventBus.emit(new EventData('mapParams', this.mapParams));
      }
    });
  }

  ngAfterViewInit(): void {
    this.eventBus.emit(new EventData('mapParams', this.mapParams));
  }

  translate(value: number): string {
    switch (value) {
      case 1:
        return 'Quick';
      case 1000:
        return 'Slow';
      default:
        return '';
    }
  }

  playPauseToggle(): void {
    this.paused = !this.paused;
    this.mapParams.paused = this.paused;
    if (!this.paused && this.displaySelection) {
      this.displaySelection = false;
    }
    this.eventBus.emit(new EventData('mapParams', this.mapParams));
  }

  loopToggle(): void {
    this.loop = !this.loop;
    this.mapParams.loop = this.loop;
    this.eventBus.emit(new EventData('mapParams', this.mapParams));
  }

  sliderChange(changeContext: ChangeContext): void {
    this.eventBus.emit(new EventData('mapTick', Math.floor(changeContext.value)));
    this.paused = this.previousPauseState;
    this.mapParams.paused = this.paused;
    this.eventBus.emit(new EventData('mapParams', this.mapParams));
  }

  sliderChangeStart(): void {
    this.previousPauseState = !!this.paused;
    this.paused = true;
    this.mapParams.paused = this.paused;
    this.eventBus.emit(new EventData('mapParams', this.mapParams));
  }

  setValue(field: string, value: any): void {
    this.mapParams[field] = (typeof value === 'string') ? value : value.value;
    this.mapParams = {...this.mapParams};
    this.eventBus.emit(new EventData('mapParams', this.mapParams));
  }

  openParamsModal(data: any): void {
    const modalRef = this.modalService.open(ParamsModalComponent, {size: 'lg', backdrop: 'static'});
    modalRef.componentInstance.data = data;
    modalRef.componentInstance.mapParams = {...this.mapParams.customParams[data.index + ''] || this.mapParams};
    modalRef.result.then((result) => {
      this.mapParams.paused = true;
      this.mapParams.customParams[data.index + ''] = {
        markerColor: result.markerColor,
        trackColor: result.trackColor,
        markerSize: result.markerSize,
        trackWidth: result.trackWidth,
        markerRadius: result.markerSize / 2,
        markerOffset: (result.markerSize - 1) / 2
      };
      this.mapParams = {...this.mapParams};
      this.eventBus.emit(new EventData('mapParams', this.mapParams));
    }, () => {
    });
  }

  getColor(index: number, field: string): string {
    return this.mapParams.customParams[index + ''] ? this.mapParams.customParams[index + ''][field] : this.mapParams[field];
  }

  hideBubble(ship: any): void {
    this.showPic = false;
    if (this.pic) {
      this.pic = undefined;
      this.eventBus.emit(new EventData('hideBubble', ship));
    }
  }

  showBubble(ship: any): void {
    this.showPic = true;
    if (!this.pic) {
      this.isImgLoaded = false;
      this.pic = ship.info.img;
      this.eventBus.emit(new EventData('showBubble', ship));
    }
  }

  toggleSelection(): void {
    this.displaySelection = !this.displaySelection;
    if (!this.displaySelection) {
      this.eventBus.emit(new EventData('hideSelection', undefined));
    }
  }

  trackByIndex(index): number {
    return index;
  }

  openAbout(): void {
    this.modalService.open(AboutComponent, {size: 'xl'}).result.then(() => {
    }, () => {
    });
  }
}
